/**
 * Representative demo universe for ?demo=1 — deterministically generated to
 * MIRROR THE REAL DATA PATHOLOGY so the effective-date logic can be proven:
 *   - most event dates are garbage (mis-dated 2027, epoch-null/0, or disagree
 *     with season_year), a minority are sane;
 *   - pursuit is ~92% "To Pursue", reps ~88% unassigned, state ~5%, champion ~2%.
 * NOT real data. The point is to show ~1,600 records DISTRIBUTE across the time
 * buckets (Just Concluded / Imminent / Closing / Active / Horizon / Pipeline /
 * Future) once timing keys off the derived effective date — not collapse into
 * Future/No-Date.
 */
import { DEMO_CHEER_GYMS } from './mockPursuits.js'

const SPORTS = [
  'Football', 'Basketball', 'Baseball', 'Softball', 'Volleyball', 'Soccer',
  'Wrestling', 'Track & Field', 'Cross Country', 'Swimming', 'Tennis', 'Golf',
  'Lacrosse', 'Hockey', 'Gymnastics', 'Cheer', 'Bowling', 'Water Polo',
]
const LEVELS = ['High School', 'College', 'Middle School', 'Club']
const REPS = ['Marcus Bell', 'Aisha Rahman', 'Dana Whitfield', 'Priya Nair', 'Cole Jensen']
const STATES = ['TX', 'CA', 'FL', 'GA', 'OH', 'NY', 'IL', 'PA', 'NC', 'ON', 'QC', 'BC']
const PURSUITS = ['Outreach In Progress', 'Outreach Completed', 'Won', 'Champion Crowned']
const SEASON_WEIGHTS = [2025, 2026, 2026, 2026, 2027] // weighted to 2026

const COUNT = 1600
const EPOCH_NULL = 0 // epoch-null sentinel → parses to no date

function gen() {
  const out = []
  for (let i = 0; i < COUNT; i++) {
    const sport = SPORTS[i % SPORTS.length]
    const level = LEVELS[i % LEVELS.length]
    const season = SEASON_WEIGHTS[i % SEASON_WEIGHTS.length]

    // A realistic minority carry a CONFIRMED near-term date (agrees with a 2026
    // season) so late-summer imminent/closing/active buckets aren't empty.
    const NEAR = ['2026-07-30', '2026-08-05', '2026-08-12', '2026-08-20', '2026-09-05', '2026-09-20']
    let sr_event_date, seasonYear = season
    if (i % 12 === 3) { sr_event_date = NEAR[i % NEAR.length]; seasonYear = 2026 } // ~8% near-term confirmed
    else {
      // Event-date pathology (deterministic by index bucket of 10):
      const mod = i % 10
      if (mod < 5) sr_event_date = `2027-0${(i % 9) + 1}-15`           // 50% garbage 2027 (disagrees)
      else if (mod < 7) sr_event_date = String(EPOCH_NULL)             // 20% epoch-null
      else if (mod < 9) sr_event_date = `${season}-0${(i % 9) + 1}-10` // 20% sane (agrees w/ season)
      else sr_event_date = ''                                          // 10% missing
    }

    const rec = {
      id: `m${i}`,
      sr_championship_name: `${STATES[i % STATES.length]} ${sport} ${level === 'College' ? 'Championship' : 'State Final'}`,
      sr_sport: sport,
      sr_level: level,
      sr_season_year: seasonYear,
      sr_event_date,
      sr_pursuit_status: i % 12 === 0 ? PURSUITS[i % PURSUITS.length] : 'To Pursue', // ~92% To Pursue
    }
    if (i % 8 === 0) rec.sr_assigned_rep = REPS[i % REPS.length]   // ~12% assigned
    if (i % 20 === 0) rec.sr_state = STATES[i % STATES.length]     // ~5% state
    if (i % 50 === 0) { rec.current_champion = `${STATES[i % STATES.length]} Academy`; rec.sr_champion_status = 'Champion Crowned' } // ~2%
    out.push(rec)
  }
  // A dozen records dated relative to NOW so "this week / next week" always
  // populates the Imminent/Closing buckets whenever the demo is opened.
  const now = new Date()
  const iso = (offset) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset).toISOString().slice(0, 10)
  ;[2, 4, 6, 9, 12, 18, 24, 28, 40, 52, 70, 85].forEach((off, k) => {
    out.push({
      id: `near${k}`,
      sr_championship_name: `${STATES[k % STATES.length]} ${SPORTS[k % SPORTS.length]} Championship`,
      sr_sport: SPORTS[k % SPORTS.length],
      sr_level: LEVELS[k % LEVELS.length],
      sr_season_year: now.getFullYear(),
      sr_event_date: iso(off),
      sr_pursuit_status: PURSUITS[k % PURSUITS.length],
      sr_assigned_rep: k % 2 === 0 ? REPS[k % REPS.length] : undefined,
    })
  })
  return out
}

/**
 * THE DEMO CHEER TITLES — crowned by the demo cheer GYMS' TEAMS.
 *
 * The Cheer space's whole hierarchy (gym → team → championship) is DERIVED from
 * the crowned-team name on a cheer championship, so `?demo=1` has to carry
 * titles whose champions actually spell a gym's team ("Lone Star Elite Cheer
 * Comets"). Without them the demo would render an empty footprint column and
 * prove nothing about the real thing.
 *
 * Two deliberate imperfections, so the demo exercises the honest paths too:
 * some gyms have NO title at all (the untouched end of the TAM), and a few
 * titles are crowned by teams that belong to no gym in the TAM — which is what
 * fills the UNMATCHED bucket the space shows rather than hides.
 */
function cheerTitles() {
  const out = []
  const now = new Date()
  const iso = (offset) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset).toISOString().slice(0, 10)
  const EVENTS = ['Summit Championship', 'The Cheerleading Worlds', 'NCA All-Star Nationals', 'UCA National Championship']
  let n = 0
  DEMO_CHEER_GYMS.forEach((gym, gi) => {
    gym.teams.forEach((team, ti) => {
      // A team can win MORE THAN ONE title — that is the whole point of the
      // team layer, so the strongest teams here win two.
      const titles = ti === 0 ? 2 : 1
      for (let k = 0; k < titles; k++) {
        const off = -(30 + n * 37)
        out.push({
          id: `cheer-title-${n}`,
          sr_championship_name: `${EVENTS[n % EVENTS.length]} — ${gym.state} ${2025 + (n % 2)}`,
          sr_sport: 'Cheer',
          sr_level: 'Club',
          sr_season_year: 2025 + (n % 2),
          sr_event_date: iso(off),
          sr_state: gym.state,
          sr_pursuit_status: 'Champion Crowned',
          sr_champion_status: 'Champion Crowned',
          current_champion: `${gym.name} ${team}`,
          sr_confirmed_date: iso(off),
        })
        n++
      }
    })
    // One gym-level title, so the "no team named" path is exercised too.
    if (gi === 0) {
      out.push({
        id: `cheer-title-gym-${gi}`,
        sr_championship_name: `${EVENTS[gi % EVENTS.length]} — Program of the Year`,
        sr_sport: 'Cheer',
        sr_level: 'Club',
        sr_season_year: 2026,
        sr_event_date: iso(-120),
        sr_state: gym.state,
        sr_pursuit_status: 'Champion Crowned',
        current_champion: gym.name,
      })
    }
  })
  // …and titles won by gyms that are NOT in the TAM. These are real work —
  // either a gym we don't have yet or a name we can't link — so they land in
  // the unmatched bucket rather than being quietly dropped.
  ;['Apex Rivals Elite Zenith', 'Twin Rivers Athletics Fury', 'Crown Point Cheer Reign'].forEach((champ, k) => {
    out.push({
      id: `cheer-title-unmatched-${k}`,
      sr_championship_name: `${EVENTS[k % EVENTS.length]} — Open Division`,
      sr_sport: 'Cheer',
      sr_level: 'Club',
      sr_season_year: 2026,
      sr_event_date: iso(-(45 + k * 30)),
      sr_pursuit_status: 'Champion Crowned',
      current_champion: champ,
    })
  })
  return out
}

export const MOCK_CHAMPIONSHIPS = [...gen(), ...cheerTitles()]
